// ZodiacHomeScreen.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import AstriaUKV2Shell from "./AstriaUKV2Shell";
import { useUKV2Colors } from "./AstriaUKV2ThemeContext";
import { UK_V2_ELEMENT_COLORS, ZODIAC_SIGN_ELEMENT } from "./astriaUKV2Theme";

// ─── UK Zodiac Homepage — per Uk.txt's "ZodiacHome_UK_v2" spec:
// 12_sign_circle layout, clean-outline sign buttons, soft-glow hover,
// click_behavior: "open_topic_room", fade_soft transition,
// uk_understated_warm tone. Each sign maps to a topic per the doc's
// sign_topics table; clicking a sign opens its reading in the Zodiac
// Personality tab (the only backend module built for per-sign content),
// carrying the sign + topic forward via the URL so that tab can frame the
// reading around the right theme.

interface ZodiacSignDef {
  id: string;
  symbol: string;
  name: string;
  topic: string;
  topicLabel: string;
}

const ZODIAC_SIGNS: ZodiacSignDef[] = [
  { id: "aries", symbol: "♈", name: "Aries", topic: "personal_questions", topicLabel: "Personal Questions" },
  { id: "taurus", symbol: "♉", name: "Taurus", topic: "finance_and_stability", topicLabel: "Finance & Stability" },
  { id: "gemini", symbol: "♊", name: "Gemini", topic: "relationships_and_study", topicLabel: "Relationships & Study" },
  { id: "cancer", symbol: "♋", name: "Cancer", topic: "family_and_home", topicLabel: "Family & Home" },
  { id: "leo", symbol: "♌", name: "Leo", topic: "confidence_and_identity", topicLabel: "Confidence & Identity" },
  { id: "virgo", symbol: "♍", name: "Virgo", topic: "work_and_routine", topicLabel: "Work & Routine" },
  { id: "libra", symbol: "♎", name: "Libra", topic: "balance_and_harmony", topicLabel: "Balance & Harmony" },
  { id: "scorpio", symbol: "♏", name: "Scorpio", topic: "deep_emotions", topicLabel: "Deep Emotions" },
  { id: "sagittarius", symbol: "♐", name: "Sagittarius", topic: "growth_and_exploration", topicLabel: "Growth & Exploration" },
  { id: "capricorn", symbol: "♑", name: "Capricorn", topic: "career_and_goals", topicLabel: "Career & Goals" },
  { id: "aquarius", symbol: "♒", name: "Aquarius", topic: "ideas_and_future", topicLabel: "Ideas & Future" },
  { id: "pisces", symbol: "♓", name: "Pisces", topic: "intuition_and_feelings", topicLabel: "Intuition & Feelings" },
];

// Places each sign button evenly around a circle — this is what makes the
// layout an actual "12_sign_circle", not just a grid of 12 boxes.
function circlePosition(index: number, total: number, radius: number) {
  const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
  return {
    left: `calc(50% + ${radius * Math.cos(angle)}px - 34px)`,
    top: `calc(50% + ${radius * Math.sin(angle)}px - 34px)`,
  };
}

const ZodiacHomeInner: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useUKV2Colors();
  const radius = 150;

  const openTopicRoom = (sign: ZodiacSignDef) => {
    navigate(`/astria-uk-v2/zodiac-personality?sign=${sign.id}&topic=${sign.topic}`);
  };

  return (
    <div>
      <div style={{ marginBottom: 28, textAlign: "center" }}>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Choose Your Sign
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Pick your sign to open its own room — a grounded, honest read, British-style.
        </p>
      </div>

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          aspectRatio: "1 / 1",
          margin: "0 auto",
        }}
      >
        {/* Center hub */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 84,
            height: 84,
            borderRadius: "50%",
            border: `1.5px solid ${colors.accent}`,
            background: colors.card,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}
        >
          <span style={{ fontSize: 20, color: colors.accent }}>✦</span>
          <span style={{ fontSize: 9.5, color: colors.mutedColor, marginTop: 2 }}>
            UK Room
          </span>
        </div>

        {ZODIAC_SIGNS.map((sign, i) => {
          const pos = circlePosition(i, ZODIAC_SIGNS.length, radius);
          const elementColor = UK_V2_ELEMENT_COLORS[ZODIAC_SIGN_ELEMENT[sign.id]] || colors.accent;
          return (
            <button
              key={sign.id}
              onClick={() => openTopicRoom(sign)}
              title={sign.topicLabel}
              className="group flex flex-col items-center justify-center rounded-full transition-all"
              style={{
                position: "absolute",
                ...pos,
                width: 68,
                height: 68,
                border: `1px solid ${colors.cardBorder}`,
                background: colors.card,
                transitionProperty: "box-shadow, border-color, transform, background-color",
                transitionDuration: "0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = elementColor;
                e.currentTarget.style.boxShadow = `0 6px 20px ${colors.highlightSoft}`;
                e.currentTarget.style.transform = "scale(1.06)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.cardBorder;
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <span style={{ fontSize: 22, color: elementColor }}>{sign.symbol}</span>
              <span style={{ fontSize: 9.5, fontWeight: 600, color: colors.bodyColor, marginTop: 2 }}>
                {sign.name}
              </span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 32,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 10,
        }}
      >
        {ZODIAC_SIGNS.map((sign) => {
          const elementColor = UK_V2_ELEMENT_COLORS[ZODIAC_SIGN_ELEMENT[sign.id]] || colors.accent;
          return (
            <button
              key={sign.id}
              onClick={() => openTopicRoom(sign)}
              className="flex items-center gap-2 rounded-xl text-left transition-all hover:scale-[1.02]"
              style={{
                padding: "10px 12px",
                border: `1px solid ${colors.cardBorder}`,
                background: colors.card,
              }}
            >
              <span style={{ fontSize: 16, color: elementColor }}>{sign.symbol}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: colors.titleColor }}>
                  {sign.name}
                </div>
                <div style={{ fontSize: 10.5, color: colors.mutedColor }}>{sign.topicLabel}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ZodiacHomeScreen: React.FC = () => (
  <AstriaUKV2Shell active="zodiac-home">
    <ZodiacHomeInner />
  </AstriaUKV2Shell>
);

export default ZodiacHomeScreen;
